// Головний результат розгляду зупиненої ПН/РК — з деталей зупинення кабінету (smkor/vat_stop/vat_stop_det):
// пояснення hdate/hnum → запит комісії про доповнення datePovOut/numPovOut (J1407802) → доповнення datePovIn/numPovIn →
// рішення actd/actn → скарга skdate/sknum → рішення за скаргою skrishdate/skrishnum. Результат рішення — зі статусу ПН:
// 3 — «зареєстровано за рішенням Комісії», інший — відмова. Строки: доповнення — 5 робочих днів з дня, наступного за
// отриманням запиту (п. 9 Порядку № 520; дата отримання невідома — рахуємо від дати запиту); рішення — 5 робочих днів після
// пояснення чи доповнення; скарга — 10 календарних днів після рішення (п. 56.23 ПКУ).
export const COMMISSION_DAYS = 5;
export const COMPLAINT_DAYS = 10;

const dmy = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const at = (iso) => new Date(`${String(iso).slice(0, 10)}T12:00:00Z`);
export function addWorkDays(iso, k) {
  const t = at(iso);
  for (let left = k; left > 0;) { t.setUTCDate(t.getUTCDate() + 1); const w = t.getUTCDay(); if (w !== 0 && w !== 6) left--; }
  return t.toISOString().slice(0, 10);
}
export function addDays(iso, k) { const t = at(iso); t.setUTCDate(t.getUTCDate() + k); return t.toISOString().slice(0, 10); }
const no = (n) => (n ? ` № ${n}` : "");

// stop — рядок списку зупинених ({ status, statusName, det }); today — YYYY-MM-DD (Київ).
// → null, якщо пояснення ще немає; інакше { tone: ok|bad|warn|info, title, detail, due? }
export function stopOutcome(stop, today) {
  const det = (stop?.det || []).filter((d) => d.hdate || d.actd).sort((a, b) => String(b.hdate || b.actd).localeCompare(String(a.hdate || a.actd)))[0];
  if (!det) return null;
  const registered = Number(stop.status) === 3;
  if (det.actd) {
    if (registered) return { tone: "ok", title: "Зареєстровано за рішенням комісії", detail: `рішення ${dmy(det.actd)}${no(det.actn)}` };
    const lines = [`рішення ${dmy(det.actd)}${no(det.actn)}`];
    if (det.skrishdate) lines.push(`рішення за скаргою ${dmy(det.skrishdate)}${no(det.skrishnum)}`);
    else if (det.skdate) lines.push(`скаргу подано ${dmy(det.skdate)}${no(det.sknum)}, розглядається`);
    const due = det.skdate ? null : addDays(det.actd, COMPLAINT_DAYS);
    if (due) lines.push(due >= today ? `скарга — до ${dmy(due)}` : `строк скарги минув ${dmy(due)}; далі — суд`);
    return { tone: "bad", title: "Відмова в реєстрації", detail: lines.join("; "), due: due && due >= today ? due : null };
  }
  if (det.datePovIn) {
    const due = addWorkDays(det.datePovIn, COMMISSION_DAYS);
    return { tone: "info", title: "Доповнення подано — на розгляді", detail: `доповнення ${dmy(det.datePovIn)}${no(det.numPovIn)}; рішення — до ${dmy(due)}`, due };
  }
  if (det.datePovOut) {
    const due = addWorkDays(det.datePovOut, COMMISSION_DAYS);
    const late = due < today;
    return { tone: late ? "bad" : "warn", title: late ? "Строк доповнення минув" : "Комісія запитала доповнення",
      detail: `запит ${dmy(det.datePovOut)}${no(det.numPovOut)}; ${late ? `строк був до ${dmy(due)} — очікується відмова` : `доповнення J1307801 — до ${dmy(due)}`}`, due: late ? null : due };
  }
  const due = addWorkDays(det.hdate, COMMISSION_DAYS);
  return { tone: "info", title: "На розгляді комісії", detail: `пояснення ${dmy(det.hdate)}${no(det.hnum)}; рішення або запит — до ${dmy(due)}`, due };
}

// Стан пояснення до зупиненої ПН і одна головна дія — з даних ДПС (det) і чернеток/подань eCabinet (drafts: { id, form, fillDate,
// status, state }; state — останнє подання). Дані ДПС мають пріоритет: якщо ДПС показує пояснення, нове J1312603 не пропонуємо.
// → { status: { tone, text }, extra?: { tone, text }, action: { kind: "create"|"continue"|"check", form, draftId?, label } | null }
const LIVE = new Set(["creating", "dps_draft", "confirmed", "signing", "sending", "sent", "unknown"]);
const WAITING = new Set(["sending", "sent", "unknown"]);
const isDraft = (d) => !d.state || d.state === "failed";
export function explainProgress({ stop, drafts = [], today }) {
  const explains = drafts.filter((d) => d.form !== "J1307801"), additions = drafts.filter((d) => d.form === "J1307801");
  const det = (stop?.det || []).filter((d) => d.hdate).sort((a, b) => String(b.hdate).localeCompare(String(a.hdate)))[0];
  const out = stopOutcome(stop, today);
  const pick = (list, f) => list.filter(f).sort((a, b) => String(b.fillDate).localeCompare(String(a.fillDate)))[0];
  const accepted = pick(explains, (d) => d.state === "accepted"), live = pick(explains, (d) => LIVE.has(d.state)), draft = pick(explains, isDraft);
  const rejected = explains.filter((d) => d.state === "rejected").length;
  const step = (list, form, noun, create) => {
    const w = pick(list, (d) => WAITING.has(d.state)), l = pick(list, (d) => LIVE.has(d.state)), dr = pick(list, isDraft);
    if (w) return { kind: "check", draftId: w.id, form, label: "Перевірити квитанцію" };
    if (l) return { kind: "continue", draftId: l.id, form, label: `Продовжити подання ${noun}` };
    if (dr) return { kind: "continue", draftId: dr.id, form, label: `Продовжити ${noun}` };
    return create;
  };
  let status, action = null, extra = null;
  if (det) status = { tone: "ok", text: `Подано ${dmy(det.hdate)}${no(det.hnum)}` };
  else if (accepted) status = { tone: "ok", text: `Прийнято ДПС, від ${dmy(accepted.fillDate)} (у списку ДПС з'явиться після оновлення)` };
  else if (live) status = { tone: "warn", text: WAITING.has(live.state) ? `Надіслано ${dmy(live.fillDate)}, чекаємо квитанцію` : `Подається, від ${dmy(live.fillDate)}` };
  else if (draft) status = { tone: "warn", text: `Чернетка від ${dmy(draft.fillDate)}, не подано` };
  else status = { tone: "bad", text: rejected ? `Не подано: ДПС не прийняла ${rejected === 1 ? "попереднє пояснення" : `${rejected} спроби`}` : "Не подано" };
  if (det || accepted) {
    const addAcc = pick(additions, (d) => d.state === "accepted");
    if (det?.datePovIn || addAcc) extra = { tone: "ok", text: `Доповнення подано ${dmy(det?.datePovIn || addAcc.fillDate)}` };
    if (out?.tone === "warn" && !addAcc) action = step(additions, "J1307801", "доповнення", { kind: "create", form: "J1307801", label: `Скласти доповнення · до ${dmy(out.due)}` });
  } else {
    action = step(explains, "J1312603", "пояснення", { kind: "create", form: "J1312603", label: "Скласти пояснення" });
  }
  return { status, extra, action };
}
